/**
 * PLACEHOLDER data for the backbone. The layout, columns, states and their
 * colors are the spec; the values are the internally-consistent example from
 * screens.md ("Austin gyms"). Replace with real data from growth-engine-api
 * (via the Orval-generated hooks) as each screen is implemented — see the app
 * README → "What's a stub".
 */
export interface DemoCampaign {
  id: string;
  name: string;
  status: "LAUNCHED" | "DRAFT";
  discovered: number;
  approved: number;
  contacts: number;
  sequences: number;
  sent: number;
  opened: number;
  signups: number;
  createdAt: string;
}

export const DEMO_CAMPAIGNS: DemoCampaign[] = [
  {
    id: "austin-gyms",
    name: "Austin gyms",
    status: "LAUNCHED",
    discovered: 128,
    approved: 41,
    contacts: 33,
    sequences: 1,
    sent: 33,
    opened: 20,
    signups: 3,
    createdAt: "2026-08-12",
  },
  {
    id: "denver-studios",
    name: "Denver studios",
    status: "DRAFT",
    discovered: 0,
    approved: 0,
    contacts: 0,
    sequences: 0,
    sent: 0,
    opened: 0,
    signups: 0,
    createdAt: "2026-08-18",
  },
];

export function findDemoCampaign(id: string | undefined): DemoCampaign | undefined {
  return DEMO_CAMPAIGNS.find((c) => c.id === id);
}

// ---------------------------------------------------------------------------
// Review Leads (screens.md §5). A representative sample; statuses drive the
// filter pills and the row treatment. Duplicate/suppressed rows stay visible
// (greyed, reason in WHY) — screening is shown, never hidden.
// ---------------------------------------------------------------------------
export type LeadStatus = "CANDIDATE" | "APPROVED" | "REJECTED" | "DUPLICATE" | "SUPPRESSED";

export interface DemoLead {
  id: string;
  name: string;
  rating: number;
  source: "PLACES" | "AI";
  status: LeadStatus;
  /** Screening reason for DUPLICATE (dedup key) / SUPPRESSED (registered:*). */
  why?: string;
}

export const DEMO_LEADS: DemoLead[] = [
  { id: "v1", name: "Barton Springs Strength", rating: 4.7, source: "PLACES", status: "CANDIDATE" },
  { id: "v2", name: "Wattage Cycling", rating: 4.6, source: "AI", status: "APPROVED" },
  { id: "v3", name: "Eastside Barbell", rating: 4.9, source: "PLACES", status: "APPROVED" },
  { id: "v4", name: "Lifted Athletics", rating: 4.8, source: "PLACES", status: "APPROVED" },
  { id: "v5", name: "Iron Peak Fitness", rating: 4.7, source: "PLACES", status: "APPROVED" },
  { id: "v6", name: "Iron Peak Fitness (2)", rating: 4.7, source: "AI", status: "DUPLICATE", why: "place:ChIJd8Z…" },
  { id: "v7", name: "Downtown Strength Co.", rating: 4.6, source: "PLACES", status: "SUPPRESSED", why: "registered:domain" },
  { id: "v8", name: "Zilker CrossFit", rating: 4.8, source: "AI", status: "CANDIDATE" },
  { id: "v9", name: "Congress Ave Fitness", rating: 4.5, source: "PLACES", status: "CANDIDATE" },
  { id: "v10", name: "Rampart Barbell", rating: 4.4, source: "AI", status: "CANDIDATE" },
  { id: "v11", name: "South Lamar Strength", rating: 4.6, source: "PLACES", status: "CANDIDATE" },
  { id: "v12", name: "Mueller Movement", rating: 4.7, source: "AI", status: "CANDIDATE" },
  { id: "v13", name: "Hyde Park Gym", rating: 4.3, source: "PLACES", status: "DUPLICATE", why: "place:ChIJ7bQ…" },
  { id: "v14", name: "ATX Powerhouse", rating: 4.9, source: "AI", status: "SUPPRESSED", why: "registered:domain" },
];

// ---------------------------------------------------------------------------
// Analytics (screens.md §10). Funnel + per-step engagement for "Austin gyms".
// ---------------------------------------------------------------------------
export interface FunnelRow {
  label: string;
  value: number;
  /** The signed-up value renders in the accent color (the conversion signal). */
  accent?: boolean;
}
export const DEMO_FUNNEL: FunnelRow[] = [
  { label: "discovered", value: 128 },
  { label: "approved", value: 41 },
  { label: "contacted", value: 33 },
  { label: "opened", value: 20 },
  { label: "replied", value: 5 },
  { label: "signed up", value: 3, accent: true },
];

export interface StepRates {
  step: string;
  open: number;
  click: number;
  reply: number;
}
export const DEMO_BY_STEP: StepRates[] = [
  { step: "step 1", open: 61, click: 18, reply: 9 },
  { step: "step 2", open: 47, click: 12, reply: 5 },
  { step: "step 3", open: 38, click: 8, reply: 3 },
];
